import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";

const YELLOW = "#ffde16";
const PALETTE = ["#ffde16", "#a98c00", "#fffbd4", "#666666", "#3a3a3a", "#cf9b00"];

const RELATIVE_PERIODS = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
];

function Stat({ label, value, sub }) {
  return (
    <div style={{ border: "1px solid #ffde16", borderRadius: 8, padding: "12px 16px", background: "rgba(0,0,0,0.5)" }}>
      <div style={{ fontSize: "0.7em", opacity: 0.7, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: "1.6em", fontWeight: "bold", color: "#ffde16" }}>{value}</div>
      {sub && <div style={{ fontSize: "0.7em", opacity: 0.65 }}>{sub}</div>}
    </div>
  );
}

function CardTitle({ children }) {
  return <h3 className="xs-section-title" style={{ marginTop: 0 }}>{children}</h3>;
}

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#000", border: "1px solid #ffde16", padding: 8, fontSize: "0.8em", color: "#ffde16" }}>
      <div style={{ marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></div>
      ))}
    </div>
  );
};

export default function AdvancedAnalytics() {
  const currentYear = new Date().getFullYear();
  const [period, setPeriod] = useState("30d");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [earliestYear, setEarliestYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [realtime, setRealtime] = useState({ active_users_5min: 0, events_last_hour: 0 });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const requestId = useRef(0);

  const load = useCallback(async (p) => {
    const request = ++requestId.current;
    setLoading(true);
    setData(null);
    try {
      const response = await api.get("/admin/analytics/advanced", { params: { period: p } });
      if (request !== requestId.current) return;
      setData(response.data);
      setEarliestYear(response.data.earliest_year ?? currentYear);
    } catch (e) {
      if (request === requestId.current) setData(null);
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, [currentYear]);

  useEffect(() => {
    load(period);
    return () => { requestId.current += 1; };
  }, [load, period]);
  useEffect(() => {
    const tick = () => api.get("/admin/analytics/realtime").then((r) => setRealtime(r.data || {})).catch(() => {});
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = sessionStorage.getItem("xs_admin_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/report.pdf?period=${encodeURIComponent(period)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("pdf failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `xaelserpent-report-${period}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { alert("could not generate pdf"); }
    finally { setDownloading(false); }
  };

  const changeYear = (year) => {
    if (year < earliestYear || year > currentYear) return;
    setSelectedYear(year);
    setPeriod((activePeriod) => {
      if (/^q[1-4]-\d{4}$/.test(activePeriod)) return `${activePeriod.slice(0, 2)}-${year}`;
      if (/^\d{4}$/.test(activePeriod)) return String(year);
      return activePeriod;
    });
  };

  const periods = [
    ...RELATIVE_PERIODS,
    ...[1, 2, 3, 4].map((quarter) => ({
      id: `q${quarter}-${selectedYear}`,
      label: `Q${quarter} ${selectedYear}`,
    })),
  ];

  return (
    <div data-testid="advanced-analytics">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {periods.map((p) => (
            <button
              key={p.id}
              className={`xs-btn ${period === p.id ? "primary" : "ghost"}`}
              onClick={() => setPeriod(p.id)}
              data-testid={`period-${p.id}`}
              style={{ padding: "6px 12px", minHeight: 0, fontSize: "0.85em" }}
            >
              {p.label}
            </button>
          ))}
          <button
            className="xs-btn ghost"
            onClick={() => changeYear(selectedYear - 1)}
            disabled={selectedYear <= earliestYear}
            aria-label="previous year"
            data-testid="previous-year"
            style={{ padding: "6px 12px", minHeight: 0, fontSize: "0.85em" }}
          >
            ‹
          </button>
          <button
            className={`xs-btn ${/^\d{4}$/.test(period) ? "primary" : "ghost"}`}
            onClick={() => setPeriod(String(selectedYear))}
            aria-label={`select year ${selectedYear}`}
            data-testid="selected-year"
            style={{ padding: "6px 12px", minHeight: 0, fontSize: "0.85em" }}
          >
            {selectedYear}
          </button>
          <button
            className="xs-btn ghost"
            onClick={() => changeYear(selectedYear + 1)}
            disabled={selectedYear >= currentYear}
            aria-label="next year"
            data-testid="next-year"
            style={{ padding: "6px 12px", minHeight: 0, fontSize: "0.85em" }}
          >
            ›
          </button>
        </div>
        <button className="xs-btn primary" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf">
          {downloading ? "generating…" : "📄 download pdf report"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 18 }}>
        <Stat label="active now (5m)" value={realtime.active_users_5min || 0} sub={`${realtime.events_last_hour || 0} events / hr`} />
        <Stat label="page views" value={(data?.page_views || 0).toLocaleString()} />
        <Stat label="unique visitors" value={(data?.unique_visitors || 0).toLocaleString()} />
        <Stat label="avg session" value={`${data?.avg_session_seconds || 0}s`} />
        <Stat label="bounce rate" value={`${Math.round((data?.bounce_rate || 0) * 100)}%`} />
        <Stat label="media plays" value={data?.media_engagement?.plays || 0}
              sub={`${Math.round((data?.media_engagement?.completion_rate || 0) * 100)}% completion`} />
      </div>

      {loading && <div style={{ opacity: 0.6, padding: 20 }}>loading…</div>}

      {data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 16 }}>
            <div style={{ border: "1px solid rgba(255,222,22,0.5)", borderRadius: 8, padding: 14 }}>
              <CardTitle>visitors & page views — {data.period_label}</CardTitle>
              <div style={{ height: 240 }}>
                <ResponsiveContainer>
                  <LineChart data={data.daily_series}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,222,22,0.15)" />
                    <XAxis dataKey="day" tick={{ fill: YELLOW, fontSize: 10 }} />
                    <YAxis tick={{ fill: YELLOW, fontSize: 10 }} />
                    <Tooltip content={<TooltipBox />} />
                    <Legend wrapperStyle={{ color: YELLOW, fontSize: 11 }} />
                    <Line type="monotone" dataKey="visitors" stroke={YELLOW} strokeWidth={2} dot={false} name="unique visitors" />
                    <Line type="monotone" dataKey="page_views" stroke="#fffbd4" strokeWidth={1.5} dot={false} name="page views" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
            <div style={{ border: "1px solid rgba(255,222,22,0.5)", borderRadius: 8, padding: 14 }}>
              <CardTitle>devices</CardTitle>
              <div style={{ height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.devices} dataKey="count" nameKey="device_type" outerRadius={80} label={{ fill: YELLOW, fontSize: 11 }}>
                      {data.devices.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<TooltipBox />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ border: "1px solid rgba(255,222,22,0.5)", borderRadius: 8, padding: 14 }}>
              <CardTitle>traffic sources</CardTitle>
              <div style={{ height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.sources} dataKey="count" nameKey="kind" outerRadius={80} label={{ fill: YELLOW, fontSize: 11 }}>
                      {data.sources.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip content={<TooltipBox />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ border: "1px solid rgba(255,222,22,0.5)", borderRadius: 8, padding: 14 }}>
              <CardTitle>hour of day</CardTitle>
              <div style={{ height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={data.hourly}>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,222,22,0.15)" />
                    <XAxis dataKey="hour" tick={{ fill: YELLOW, fontSize: 10 }} />
                    <YAxis tick={{ fill: YELLOW, fontSize: 10 }} />
                    <Tooltip content={<TooltipBox />} />
                    <Bar dataKey="count" fill={YELLOW} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div style={{ border: "1px solid rgba(255,222,22,0.5)", borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <CardTitle>top referrers</CardTitle>
            {data.referrers.length === 0 ? <div style={{ opacity: 0.6 }}>no referrers in range yet.</div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85em" }}>
                <thead>
                  <tr style={{ color: YELLOW }}>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>source</th>
                    <th style={{ textAlign: "right", padding: "6px 8px" }}>visitors</th>
                    <th style={{ textAlign: "right", padding: "6px 8px" }}>events</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrers.map((r) => (
                    <tr key={r.label} style={{ borderTop: "1px dashed rgba(255,222,22,0.15)" }}>
                      <td style={{ padding: "6px 8px", color: "#fffbd4" }}>{r.label}</td>
                      <td style={{ textAlign: "right", padding: "6px 8px" }}>{r.visitors}</td>
                      <td style={{ textAlign: "right", padding: "6px 8px", opacity: 0.8 }}>{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {data.top_posts.length > 0 && (
            <div style={{ border: "1px solid rgba(255,222,22,0.5)", borderRadius: 8, padding: 14 }}>
              <CardTitle>top posts</CardTitle>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85em" }}>
                <thead>
                  <tr style={{ color: YELLOW }}>
                    <th style={{ textAlign: "left", padding: "6px 8px" }}>post</th>
                    <th style={{ textAlign: "right", padding: "6px 8px" }}>views</th>
                    <th style={{ textAlign: "right", padding: "6px 8px" }}>unique</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_posts.map((p) => (
                    <tr key={p.slug} style={{ borderTop: "1px dashed rgba(255,222,22,0.15)" }}>
                      <td style={{ padding: "6px 8px" }}><a href={`/articles/${p.slug}`} style={{ color: "#fffbd4" }}>{p.slug}</a></td>
                      <td style={{ textAlign: "right", padding: "6px 8px" }}>{p.views}</td>
                      <td style={{ textAlign: "right", padding: "6px 8px", opacity: 0.8 }}>{p.visitors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: "0.75em", opacity: 0.55, textAlign: "center" }}>
            privacy-first: visitor IPs are hashed with a daily-rotating salt — no cookies, no PII.
          </div>
        </>
      )}
    </div>
  );
}
